import { Injectable } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  private readonly emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  private readonly passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/
  private readonly nifPattern = /^[0-9]{8}[A-Z]$/
  private readonly niePattern = /^[XYZ][0-9]{7}[A-Z]$/
  private readonly cifPattern = /^[A-Z][0-9]{7}[0-9A-Z]$/
  private readonly phonePattern = /^[0-9]{3}-[0-9]{3}-[0-9]{3}$|^[0-9]{9}$/
  private readonly expirationDatePattern = /^(0[1-9]|1[0-2])\/([0-9]{2})$/
  private readonly cvcPattern = /^[0-9]{3,4}$/
  private readonly postalCodePattern = /^[0-9]{5}$/

  private validationMessages: { [key: string]: string } = {
    required: 'Este campo es obligatorio.',
    invalidLogin: 'Usuario o contraseña incorrectos.',
    invalidEmailAddress: 'El correo electrónico no tiene un formato válido.',
    invalidPassword: 'Contraseña inválida',
    passwordsDoNotMatch: 'Las contraseñas no coinciden.',
    termsRequired: 'Debes aceptar los términos y condiciones.',
    pepRequired: 'Debes confirmar que no eres un PEP o relacionado.',
    invalidNIF: 'NIF inválido (8 números y 1 letra).',
    invalidCIF: 'CIF inválido (1 letra - 7 números - 1 letra(o numero de control)).',
    invalidPhone: 'Teléfono inválido (9 números).',
    invalidCardNumber: 'Número de tarjeta inválido (16 números).',
    invalidExpirationDate: 'Fecha de expiración inválida o tarjeta expirada (MM/YY).',
    invalidCVC: 'CVC inválido (3 o 4 dígitos).',
    invalidPostalCode: 'Código postal inválido (5 dígitos entre 01000 y 52999).',
    integer: 'El valor debe ser un número entero.',
  }

  getValidatorErrorMessage(validatorName: string): string {
    return this.validationMessages[validatorName] || 'Error desconocido.'
  }

  emailValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (value && !this.emailPattern.test(value)) {
        return { invalidEmailAddress: true }
      }
      return null
    }
  }

  passwordValidator() {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value && this.passwordPattern.test(control.value)) {
        return null
      }
      return { invalidPassword: true }
    }
  }

  getTooltipMessage(key: string): string {
    const messages: { [key: string]: string } = {
      invalidPassword: `
			La contraseña debe tener al menos:
			<ul style="margin: 0; padding-left: 1rem">
			  <li>8 caracteres</li>
			  <li>Una mayúscula</li>
			  <li>Una minúscula</li>
			  <li>Un número</li>
			  <li>Un carácter especial</li>
			</ul>
		  `,
    }

    return messages[key] || ''
  }

  /**
   * Validates that two password fields match.
   * @param passwordFieldName - The name of the password field to compare against
   * @returns A validator function that checks if the passwords match
   */
  confirmPasswordValidator(passwordFieldName: string) {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const formGroup = control.parent
      if (formGroup) {
        const passwordControl = formGroup.get(passwordFieldName)
        if (passwordControl && passwordControl.value !== control.value) {
          return { passwordsDoNotMatch: true }
        }
      }
      return null
    }
  }

  /**
   * Validates that terms and conditions have been accepted.
   * @returns A validator function that checks if terms are accepted
   */
  termsValidator() {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      return control.value ? null : { termsRequired: true }
    }
  }

  /**
   * Validates that PEP (Politically Exposed Person) status has been confirmed.
   * @returns A validator function that checks if PEP status is confirmed
   */
  pepValidator() {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      return control.value ? null : { pepRequired: true }
    }
  }

  /**
   * Validates Spanish NIF (Tax Identification Number) or NIE (Foreigner Identification Number).
   * NIF format: 8 digits + 1 uppercase letter
   * NIE format: X/Y/Z + 7 digits + 1 uppercase letter
   * @returns A validator function that checks if the NIF/NIE format is valid
   */
  nifValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      const upperValue = value.toUpperCase()

      if (!this.nifPattern.test(upperValue) && !this.niePattern.test(upperValue)) {
        return { invalidNIF: true }
      }

      return null
    }
  }

  /**
   * Validates Spanish CIF (Company Identification Number).
   * Format: 1 letter + 7 digits + 1 letter/number
   * @returns A validator function that checks if the CIF format is valid
   */
  cifValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      const upperValue = value.toUpperCase()

      if (!this.cifPattern.test(upperValue)) {
        return { invalidCIF: true }
      }

      return null
    }
  }

  /**
   * Validates Spanish phone number format.
   * Format: 111-111-111
   * @returns A validator function that checks if the phone number format is valid
   */
  phoneValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      if (!this.phonePattern.test(value)) {
        return { invalidPhone: true }
      }

      return null
    }
  }

  /**
   * Validates credit card number using Luhn algorithm.
   * @returns A validator function that checks if the card number is valid
   */
  cardNumberValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.replace(/\s/g, '')
      if (!value) return null

      let sum = 0
      let isEven = false

      for (let i = value.length - 1; i >= 0; i--) {
        let digit = Number.parseInt(value.charAt(i), 10)

        if (isEven) {
          digit *= 2
          if (digit > 9) {
            digit -= 9
          }
        }

        sum += digit
        isEven = !isEven
      }

      if (sum % 10 !== 0) {
        return { invalidCardNumber: true }
      }

      return null
    }
  }

  expirationDateValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      const match = value.match(this.expirationDatePattern)
      if (!match) {
        return { invalidExpirationDate: true }
      }

      const month = Number.parseInt(match[1], 10)
      const year = Number.parseInt(match[2], 10)

      if (month < 1 || month > 12) {
        return { invalidExpirationDate: true }
      }

      const currentDate = new Date()
      const currentYear = currentDate.getFullYear() % 100
      const currentMonth = currentDate.getMonth() + 1

      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        return { invalidExpirationDate: true }
      }

      return null
    }
  }

  cvcValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      if (!this.cvcPattern.test(value)) {
        return { invalidCVC: true }
      }

      return null
    }
  }

  static integerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      return !Number.isInteger(+value) ? { integer: true } : null
    }
  }

  postalCodeValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) return null

      if (!this.postalCodePattern.test(value)) {
        return { invalidPostalCode: true }
      }

      const postalCode = Number.parseInt(value, 10)
      if (postalCode < 1000 || postalCode > 52999) {
        return { invalidPostalCode: true }
      }

      return null
    }
  }
}
